import _ from "underscore";
import { onPatch } from "mobx-state-tree";

import Backbone from "pages/common/lib/backbone-helpers";
import CONSTANTS from "const/constants";

const { BACKBONE_EVENTS, MST_EVENTS } = CONSTANTS;

const ConnectModelToStore = (_model, store, getStoreFromParent = false) => {
    const Model = _model.extend({
        initialize: function (_attrs) {
            Model.__super__.initialize.apply(this, arguments);
            if (getStoreFromParent) {
                this.store = store.__getModelById(this.id, true);
            } else {
                this.store = store;
                const attrs = _attrs || { ...store };
                this.set(attrs, { preventStoreUpdate: true });
            }
            this?.store?.__setBackboneModel?.(this);

            this.on(
                BACKBONE_EVENTS.CHANGE,
                (model, options) => {
                    if (!options.preventStoreUpdate) {
                        this.updateStore();
                    }
                },
                this
            );

            if (!_.isUndefined(this.store)) {
                onPatch(this.store, (patch) => {
                    if (!_.isUndefined(this.id)) {
                        switch (patch.op) {
                            case MST_EVENTS.REPLACE:
                                this.set(
                                    { ...this.store },
                                    { preventStoreUpdate: true }
                                );
                                break;
                        }
                    }
                });
            }
        },

        updateStore: function (data = this.toJSON({ rawAttrs: true })) {
            this.store.__update(data);
        },

        toJSON: function (options) {
            if (options?.rawAttrs) {
                return { ...this.attributes };
            }
            return Model.__super__.toJSON.apply(this, arguments);
        },

        url: function () {
            return ``;
        },

        parse: function (res) {
            return res;
        },

        reset: function (data) {
            this.updateStore(data);
        },

        sync: async function (method, model, options) {
            const { error, success } = options;
            switch (method) {
                case `create`:
                    console.warn(
                        `Migrated model is attempting to sync a CREATE method`
                    );
                    break;
                case `read`:
                    try {
                        const response = await this.store.__syncRead();
                        success && success(this, response, options);
                    } catch (err) {
                        console.error(err);
                        error && error(this, err, options);
                    }
                    break;
                case `update`:
                    console.warn(
                        `Migrated model is attempting to sync an UPDATE method`
                    );
                    break;
                case `delete`:
                    console.warn(
                        `Migrated model is attempting to sync a DELETE method`
                    );
                    break;
                default:
                    return Backbone.sync.apply(this, arguments);
            }
        },

        /**
         * @summary Set a hash of model attributes on the object, firing a "change" event.
         * This is a copy of the original method. The only change is that 'unset'
         * properties are set to 'undefined' instead of being deleted.
         * @param {string} key
         * @param {*} val
         * @param {Object} options
         * @return {BackboneModel} returns this model
         */
        set: function (key, val, options) {
            if (key === null) return this;

            let attrs;
            if (typeof key === `object`) {
                attrs = key;
                options = val;
            } else {
                (attrs = {})[key] = val;
            }

            options || (options = {});

            if (!this._validate(attrs, options)) return false;

            const unset = options.unset;
            const silent = options.silent;
            const changes = [];
            const changing = this._changing;
            this._changing = true;

            if (!changing) {
                this._previousAttributes = _.clone(this.attributes);
                this.changed = {};
            }

            const current = this.attributes;
            const changed = this.changed;
            const prev = this._previousAttributes;

            for (const attr in attrs) {
                val = attrs[attr];
                if (!_.isEqual(current[attr], val)) changes.push(attr);
                if (!_.isEqual(prev[attr], val)) {
                    changed[attr] = val;
                } else {
                    delete changed[attr];
                }
                // unset ? delete current[attr] : current[attr] = val;
                current[attr] = unset ? undefined : val;
            }

            if (this.idAttribute in attrs) this.id = this.get(this.idAttribute);

            if (!silent) {
                if (changes.length) this._pending = options;
                for (let i = 0; i < changes.length; i++) {
                    this.trigger(
                        `change:${changes[i]}`,
                        this,
                        current[changes[i]],
                        options
                    );
                }
            }

            if (changing) return this;
            if (!silent) {
                while (this._pending) {
                    options = this._pending;
                    this._pending = false;
                    this.trigger(`change`, this, options);
                }
            }
            this._pending = false;
            this._changing = false;
            return this;
        }
    });
    return Model;
};

export default ConnectModelToStore;
